import { useEffect, useRef, useState } from "react"

import {
  ArtistWithRoleEdge,
  Group,
  InfoModalQuery,
  RoleType,
} from "../../../../generated/graphql"
import useIntl from "../../../../hooks/use-intl"
import useTranslate, { Namespaces } from "../../../../hooks/use-translate"
import { TranslationKey } from "../../../../types"
import { secondsToMinutes } from "../../../../utilities/common"
import { DetailpageData } from "../../DetailStore"

type ArtistWithRole = {
  node: Pick<ArtistWithRoleEdge["node"], "id" | "name" | "sortName">
  role: Pick<ArtistWithRoleEdge["role"], "id" | "type" | "displayName">
}

export type CreditsList = {
  titleTranslationKey: TranslationKey<Namespaces.Default>
  values: string[]
}[]

type GroupWithType = Pick<Group, "id" | "name" | "typeDisplayName">

export function useCredits(
  node: InfoModalQuery["node"] | DetailpageData,
  withRole = true,
) {
  const [result, setResult] = useState<CreditsList>([])

  useEffect(() => {
    if (
      node &&
      (node.__typename === "VodConcert" ||
        node.__typename === "Video" ||
        node.__typename === "Album" ||
        node.__typename === "LiveConcert")
    ) {
      const creditsList: CreditsList = []
      const artistsWithRole: ArtistWithRole[] =
        "artists" in node && node.artists?.edges ? node.artists?.edges : []
      const groups: GroupWithType[] = "groups" in node ? node.groups : []
      // Get conductors from list of artists
      const conductors = artistsWithRole.filter(
        (artistWithRole) => artistWithRole.role.type === RoleType.Conductor,
      )
      // Get soloists (Vocal/Instrumental) from list of artists and sort by name
      const soloists = artistsWithRole.filter(
        (artistWithRole) =>
          artistWithRole.role.type === RoleType.VocalSoloist ||
          artistWithRole.role.type === RoleType.InstrumentalSoloist,
      )

      if (soloists.length > 0) {
        creditsList.push({
          titleTranslationKey: "soloist",
          values: soloists.map(
            (soloist) =>
              `${soloist.node.name}` +
              (withRole ? ` (${soloist.role.displayName})` : ""),
          ),
        })
      }

      if (groups.length > 0) {
        creditsList.push({
          titleTranslationKey: "ensemble",
          values: groups.map((group) => group.name),
        })
      }

      if (conductors.length > 0) {
        creditsList.push({
          titleTranslationKey: "conductor",
          values: conductors.map((conductor) => conductor.node.name),
        })
      }
      setResult(creditsList)
    }
  }, [node, withRole])

  return result.length > 0 ? result : undefined
}

export const useMetaInfo = ({ node }: { node: InfoModalQuery["node"] }) => {
  // Helper to add a new string to the given group index
  const t = useTranslate()
  const { dateFormat } = useIntl()
  function addMetaInfo(
    metaInfo: string[][],
    groupIndex: number,
    value: string,
  ) {
    metaInfo[groupIndex] = [...(metaInfo[groupIndex] || []), value]
    return metaInfo
  }
  /**
   * Array of grouped meta info strings. The strings are grouped by their relevance and will be displayed with a
   * divider between them.
   * e.g. [
   *   ['Total duration: 01:23'],
   *   ['Release date: 01.01.2021', 'Recorded date: 01.01.2020']
   * ]
   */
  const metaInfo = useRef<string[][]>([])
  useEffect(() => {
    if (node) {
      metaInfo.current = []
      /**
       * Album metadata should include:
       * - Total album duration
       * - Release date
       * - Album UPCs (SD, HD, Dolby Atmos)
       * - Phonographic copyright
       * - Copyright
       */
      if (node.__typename === "Album") {
        metaInfo.current = addMetaInfo(
          metaInfo.current,
          0,
          t("metaInfoLabel__album_duration", [
            t("video__duration_in_minutes", [
              secondsToMinutes(node.totalDuration),
            ]),
          ]),
        )
        if (node.releaseDate)
          metaInfo.current = addMetaInfo(
            metaInfo.current,
            1,
            t("metaInfoLabel__album_releaseDate", [
              dateFormat(new Date(node.releaseDate), "short"),
            ]),
          )
        if (node.upc)
          metaInfo.current = addMetaInfo(
            metaInfo.current,
            2,
            t("metaInfoLabel__album_upc", [node.upc]),
          )
        if (node.atmosUpc)
          metaInfo.current = addMetaInfo(
            metaInfo.current,
            2,
            t("metaInfoLabel__album_atmosUpc", [node.atmosUpc]),
          )
        metaInfo.current = addMetaInfo(
          metaInfo.current,
          3,
          t("metaInfoLabel__copyright", [node.albumCopyright]),
        )
        metaInfo.current = addMetaInfo(
          metaInfo.current,
          3,
          t("metaInfoLabel__phonographicCopyright", [
            node.phonographicCopyright,
          ]),
        )
      }

      /**
       * Video metadata should include:
       * - Video director
       * - Photographer
       * - Phonographic copyright
       * - Copyright
       * - Production date
       * - Available until date
       * - Total duration
       * - Concert ISRC
       */
      if (
        node.__typename === "LiveConcert" ||
        node.__typename === "Video" ||
        node.__typename === "VodConcert"
      ) {
        if (node.videoDirector)
          metaInfo.current = addMetaInfo(
            metaInfo.current,
            0,
            t("metaInfoLabel__video_director", [node.videoDirector]),
          )
        if (node.photographer)
          metaInfo.current = addMetaInfo(
            metaInfo.current,
            0,
            t("metaInfoLabel__video_photographer", [node.photographer]),
          )
        if (node.production)
          metaInfo.current = addMetaInfo(metaInfo.current, 1, node.production)
        if (node.copyright)
          metaInfo.current = addMetaInfo(metaInfo.current, 1, node.copyright)
        if (node.__typename !== "LiveConcert" && node.productionDate)
          metaInfo.current = addMetaInfo(
            metaInfo.current,
            2,
            t("metaInfoLabel__video_dateOfConcert", [
              dateFormat(new Date(node.productionDate), "short"),
            ]),
          )
        if (node.__typename !== "LiveConcert" && node.takedownDate)
          metaInfo.current = addMetaInfo(
            metaInfo.current,
            2,
            t("video__available_until_day", [
              dateFormat(new Date(node.takedownDate), "short"),
            ]),
          )
        metaInfo.current = addMetaInfo(
          metaInfo.current,
          2,
          t("metaInfoLabel__video_concertDuration", [
            t("video__duration_in_minutes", [
              secondsToMinutes(
                node.__typename === "Video"
                  ? node.duration
                  : node.totalDuration,
              ),
            ]),
          ]),
        )
        if (node.isrc)
          metaInfo.current = addMetaInfo(
            metaInfo.current,
            3,
            t("concert_isrc_label", [node.isrc]),
          )
      }
    }
  }, [node])
  return metaInfo.current
}

export const getBio = ({ node }: { node: InfoModalQuery["node"] }) => {
  if (!node || node.__typename === "Album" || node.__typename === "Group")
    return
  let content
  if (node.__typename === "Video") {
    content = node.description
  }
  if (node.__typename === "VodConcert" || node.__typename === "LiveConcert") {
    content = node.shortDescription
  }
  if (node.__typename === "Artist") {
    //title = "Artist biography"
    content = node.biography
  }
  if (node.__typename === "Partner" || node.__typename === "Epoch") {
    //title = "About us"
    content = node.description
  }
  return { content }
}
